import { useEffect, useRef } from "react";

import { ChatBody } from "src/base-components/Chat/ChatBody";
import { ChatEmptyState } from "src/base-components/Chat/ChatEmptyState";
import { ChatInputArea } from "src/base-components/Chat/ChatInputArea";
import { ChatMessageList } from "src/base-components/Chat/ChatMessageList";
import type { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { useFileUploadDropzone } from "src/base-components/FileUpload";
import {
  COPILOT_CHAT_ACCEPTED_FILE_TYPES,
  COPILOT_CHAT_MAX_FILE_SIZE_BYTES,
  MAX_COPILOT_CHAT_ATTACHMENTS,
} from "src/unifiedCopilot/chat/attachments/constants";
import { useChatAttachments } from "src/unifiedCopilot/chat/attachments/useChatAttachments";
import { UnifiedCopilotPromptBox } from "src/unifiedCopilot/chat/promptBox/UnifiedCopilotPromptBox";
import {
  isSessionBusy,
  isSessionInFlight,
  isSessionInterruptible,
} from "src/unifiedCopilot/chat/UnifiedCopilotChatSessionContext";
import { UnifiedCopilotMessages } from "src/unifiedCopilot/chat/UnifiedCopilotMessages";
import { UnifiedCopilotSessionLoadingSkeleton } from "src/unifiedCopilot/chat/UnifiedCopilotSessionLoadingSkeleton";
import { useChatScroll } from "src/unifiedCopilot/chat/useChatScroll";
import { useUnifiedCopilotChatSession } from "src/unifiedCopilot/chat/useUnifiedCopilotChatSession";
import { useRegisteredSuggestionPrompts } from "src/unifiedCopilot/context/internalHooks";

// Shown while no mounted surface declares its own suggestions
// (`useCopilotSuggestionPrompts`).
const DEFAULT_SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  { label: "What does this flow do?", prompt: "What does this flow do?" },
  {
    label: "Explain the difference between sandbox and live",
    prompt: "Explain the difference between sandbox and live.",
  },
  {
    label: "Which version is serving live traffic?",
    prompt: "Which version is serving live traffic?",
  },
];

const UnifiedCopilotPageEmptyState: React.FC<{
  prompt: React.ReactNode;
}> = ({ prompt }) => (
  <div
    className="flex h-full items-center justify-center p-5"
    data-loc="unified-copilot-page-empty-state"
  >
    <div className="flex w-110 max-w-full flex-col gap-2.5">
      <h2 className="font-inter-medium-13px text-gray-800">
        Where should we start?
      </h2>
      {prompt}
    </div>
  </div>
);

export const UnifiedCopilotChat: React.FC<{
  variant?: "compact" | "page";
}> = ({ variant = "compact" }) => {
  const registeredSuggestionPrompts = useRegisteredSuggestionPrompts();
  const suggestionPrompts =
    registeredSuggestionPrompts.length > 0
      ? registeredSuggestionPrompts
      : DEFAULT_SUGGESTION_PROMPTS;
  const {
    messages,
    sessionState,
    queuedDrafts,
    contextualEntities,
    send,
    stop,
    dismissContextualEntity,
    restoreContextualEntities,
    sessionId,
    liveStatusDetail,
  } = useUnifiedCopilotChatSession();

  // A freshly mounted composer starts with the page's full context: a
  // dismissal is transient, and reopening the copilot is the way to recover
  // from an accidental one.
  useEffect(() => {
    restoreContextualEntities();
  }, [restoreContextualEntities]);

  const {
    attachments,
    addFile,
    removeAttachment,
    clearAttachments,
    hasPendingUploads,
    isFull,
  } = useChatAttachments({ resetKey: sessionId });

  const listRef = useRef<HTMLUListElement>(null);
  const lastTurnRef = useRef<HTMLLIElement>(null);

  const { reservesTurnHeight, snapPromptToTop } = useChatScroll({
    listRef,
    lastTurnRef,
    messages,
    isActive: isSessionInFlight(sessionState),
    queuedCount: queuedDrafts.length,
  });

  const { getRootProps, getInputProps, isDragActive, open } =
    useFileUploadDropzone({
      onFileSelect: addFile,
      multiple: true,
      disabled: isFull,
      accept: COPILOT_CHAT_ACCEPTED_FILE_TYPES,
      maxSize: COPILOT_CHAT_MAX_FILE_SIZE_BYTES,
      maxFiles: MAX_COPILOT_CHAT_ATTACHMENTS - attachments.length,
    });

  const handleSubmit = (content: string) => {
    if (
      (!content.trim() && attachments.length === 0) ||
      isSessionBusy(sessionState) ||
      hasPendingUploads
    ) {
      return;
    }
    snapPromptToTop();
    send(
      content,
      attachments.flatMap((attachment) =>
        attachment.status === "uploaded"
          ? [
              {
                fileId: attachment.fileId,
                name: attachment.file.name,
                fileType: attachment.file.type || null,
              },
            ]
          : [],
      ),
    );
    clearAttachments();
  };

  const isEmpty =
    sessionState === "idle" &&
    messages.length === 0 &&
    queuedDrafts.length === 0;
  const prompt = (
    <UnifiedCopilotPromptBox
      attachDisabled={isFull}
      attachments={attachments}
      buttonMode={isSessionInterruptible(sessionState) ? "stop" : "send"}
      contextualEntities={contextualEntities}
      submitDisabled={isSessionBusy(sessionState)}
      onAttachClick={open}
      onDismissContextualEntity={dismissContextualEntity}
      onRemoveAttachment={removeAttachment}
      onStop={stop}
      onSubmit={handleSubmit}
    />
  );

  return (
    <div
      {...getRootProps({
        "data-loc": "unified-copilot-dropzone",
        className: "relative h-full",
      })}
    >
      <input
        {...getInputProps()}
        data-loc="unified-copilot-attachments-input"
      />
      {variant === "page" && isEmpty ? (
        <UnifiedCopilotPageEmptyState prompt={prompt} />
      ) : (
        <ChatBody dataLoc="unified-copilot-chat">
          <div
            className={
              variant === "page"
                ? "mx-auto flex min-h-0 w-full max-w-194 flex-1 flex-col"
                : "flex min-h-0 flex-1 flex-col"
            }
          >
            <ChatMessageList ref={listRef} smoothScroll>
              {sessionState === "restoring" ? (
                <UnifiedCopilotSessionLoadingSkeleton />
              ) : isEmpty ? (
                <ChatEmptyState
                  dataLoc="unified-copilot-empty-state"
                  prompts={suggestionPrompts}
                  variant="new"
                  onPromptClick={handleSubmit}
                />
              ) : (
                <UnifiedCopilotMessages
                  isActive={sessionState === "running"}
                  lastTurnRef={lastTurnRef}
                  liveStatusDetail={liveStatusDetail}
                  messages={messages}
                  queuedDrafts={queuedDrafts}
                  reservesTurnHeight={reservesTurnHeight}
                />
              )}
            </ChatMessageList>

            <ChatInputArea>{prompt}</ChatInputArea>
          </div>
        </ChatBody>
      )}
      {isDragActive && (
        // Kept interactive on purpose: the drop lands on this overlay and
        // bubbles to the dropzone root instead of reaching the Lexical
        // editor, which would otherwise handle the drop itself.
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border border-indigo-400 border-dashed bg-indigo-50/90"
          data-loc="unified-copilot-drop-overlay"
        >
          <span className="font-inter-medium-12px text-indigo-600">
            Drop files to attach
          </span>
        </div>
      )}
    </div>
  );
};
